import { useState, useEffect } from 'react';
import { useGroupStore } from '@/stores/groupStore';
import type { Group } from '@/types';

interface ShareToGroupModalProps {
  blahblahId: string;
  onClose: () => void;
}

export function ShareToGroupModal({ blahblahId, onClose }: ShareToGroupModalProps) {
  const { postBlahblahToGroup, loadMyGroupsForShare } = useGroupStore();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [sharingTo, setSharingTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMyGroupsForShare()
      .then((items) => {
        setGroups(items.filter((g) => g.myStatus === 'active'));
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load groups');
        setLoading(false);
      });
  }, [loadMyGroupsForShare]);

  const handleShare = async (group: Group) => {
    setSharingTo(group.id);
    setError(null);
    try {
      await postBlahblahToGroup(group.id, blahblahId);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to share';
      if (message.includes('409') || message.includes('duplicate') || message.includes('UNIQUE')) {
        setError(`Already shared to ${group.name}`);
      } else {
        setError(message);
      }
      setSharingTo(null);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 max-w-md mx-auto bg-card rounded-xl shadow-xl border border-border overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-card-foreground">Share to Group</h2>
        </div>

        {/* Body */}
        <div className="max-h-80 overflow-y-auto">
          {loading && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          )}

          {!loading && groups.length === 0 && (
            <p className="text-center text-muted-foreground py-8 px-4">
              You are not a member of any groups yet.
            </p>
          )}

          {error && (
            <div className="mx-4 mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {!loading && groups.map((group) => {
            const isSharing = sharingTo === group.id;
            return (
              <button
                key={group.id}
                onClick={() => handleShare(group)}
                disabled={isSharing}
                className="w-full flex items-center gap-3 p-3 hover:bg-muted transition-colors disabled:opacity-50 text-left"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shrink-0"
                  style={{ backgroundColor: group.color || '#7c3aed' }}
                >
                  {group.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-card-foreground text-sm truncate">{group.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {group.memberCount} member{group.memberCount === 1 ? '' : 's'}
                  </p>
                </div>
                {isSharing && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary shrink-0" />
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-border">
          <button
            onClick={onClose}
            className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
